/**
 * Comic Speech, Thought, and Whisper Bubbles with pointer tails.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export type BubbleType = 'speech' | 'thought' | 'whisper' | 'shout';

export interface BubbleProps {
  text: string;
  type?: BubbleType;
  width?: number;
  height?: number;
  bubbleColor?: string;
  strokeColor?: string;
  textColor?: string;
  fontSize?: number;
  tailX?: number;
  tailY?: number;
}

export class Bubble extends SVGObject {
  public props: BubbleProps;

  constructor(config: SVGObjectConfig & { props?: BubbleProps }) {
    super({ ...config, type: 'bubble' });
    this.props = {
      text: 'Speech',
      type: 'speech',
      width: 220,
      height: 100,
      bubbleColor: '#1e293b',
      strokeColor: '#38bdf8',
      textColor: '#f8fafc',
      fontSize: 16,
      tailX: 0,
      tailY: 80,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const w = this.props.width || 220;
    const h = this.props.height || 100;
    const tx = this.props.tailX ?? 0;
    const ty = this.props.tailY ?? 80;
    const bType = this.props.type || 'speech';

    if (bType === 'speech') {
      // Rounded rect with speech tail
      const path = Renderer.createElement('path');
      const r = 16;
      const hw = w / 2;
      const hh = h / 2;
      const d = `
        M ${-hw + r} ${-hh}
        L ${hw - r} ${-hh}
        Q ${hw} ${-hh} ${hw} ${-hh + r}
        L ${hw} ${hh - r}
        Q ${hw} ${hh} ${hw - r} ${hh}
        L 20 ${hh}
        L ${tx} ${ty}
        L -10 ${hh}
        L ${-hw + r} ${hh}
        Q ${-hw} ${hh} ${-hw} ${hh - r}
        L ${-hw} ${-hh + r}
        Q ${-hw} ${-hh} ${-hw + r} ${-hh}
        Z
      `;
      Renderer.setAttributes(path, {
        d,
        fill: this.props.bubbleColor || '#1e293b',
        stroke: this.props.strokeColor || '#38bdf8',
        'stroke-width': 2,
        filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))'
      });
      this.container.appendChild(path);
    } else if (bType === 'thought') {
      // Cloud thought bubble
      const ellipse = Renderer.createElement('ellipse');
      Renderer.setAttributes(ellipse, {
        cx: 0,
        cy: 0,
        rx: w / 2,
        ry: h / 2,
        fill: this.props.bubbleColor || '#1e293b',
        stroke: this.props.strokeColor || '#94a3b8',
        'stroke-width': 2,
        'stroke-dasharray': '4,4'
      });
      this.container.appendChild(ellipse);

      // Trailing thought dots
      const d1 = Renderer.createElement('circle');
      Renderer.setAttributes(d1, { cx: tx * 0.4, cy: (h / 2) * 0.7 + 10, r: 8, fill: this.props.bubbleColor || '#1e293b', stroke: '#94a3b8', 'stroke-width': 1.5 });
      const d2 = Renderer.createElement('circle');
      Renderer.setAttributes(d2, { cx: tx * 0.8, cy: (h / 2) * 0.7 + 26, r: 5, fill: this.props.bubbleColor || '#1e293b', stroke: '#94a3b8', 'stroke-width': 1.5 });
      this.container.appendChild(d1);
      this.container.appendChild(d2);
    }

    // Text content
    const textEl = Renderer.createElement('text');
    Renderer.setAttributes(textEl, {
      x: 0,
      y: 0,
      'font-size': this.props.fontSize || 16,
      'font-family': 'Inter, sans-serif',
      'font-weight': '500',
      fill: this.props.textColor || '#f8fafc',
      'text-anchor': 'middle',
      'dominant-baseline': 'central'
    });

    // Handle multiline
    const lines = (this.props.text || '').split('\n');
    lines.forEach((line, i) => {
      const tspan = Renderer.createElement('tspan');
      tspan.textContent = line;
      tspan.setAttribute('x', '0');
      tspan.setAttribute('dy', i === 0 ? `${-(lines.length - 1) * 9}px` : '18px');
      textEl.appendChild(tspan);
    });

    this.container.appendChild(textEl);
  }

  public setText(text: string): void {
    this.props.text = text;
    this.render();
  }
}
